import { MasterService } from './master-service';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { Application, Container } from '@tencent/alloy-renderer';
import { IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';

/**
 * 母版manager
 * 维护、缓存所有的母版实例
 */
export class MasterManager {
    private scale: number;
    private masterCacheMap: { [id: string]: MasterService };
    private currentMasterId: string;
    private getApplication: () => Application;
    private handleStableLayerUpdate: (props: Partial<IRenderProps>) => void;

    public constructor(
        scale = 1,
        getApplication: () => Application,
        handleStableLayerUpdate: (props: Partial<IRenderProps>) => void,
    ) {
        this.scale = scale;
        this.masterCacheMap = {};
        this.getApplication = getApplication;
        this.handleStableLayerUpdate = handleStableLayerUpdate;
    }

    public buildMasterByCache(masterModel: IMasterModel) {
        // 查找当前model的母版是否在缓存中
        let masterService: MasterService | undefined = this.getMasterFromCache(masterModel);
        if (!masterService) { // 没缓存过就构建个新的模板缓存
            masterService = this.buildMasterService(masterModel);
        }
        return masterService;
    }

    /**
     * 查找缓存中是否有该母版
     * @param masterModel
     */
    public getMasterFromCache(masterModel: IMasterModel): MasterService | undefined {
        const masterId = this.getMasterId(masterModel);
        return this.masterCacheMap[masterId];
    }

    /**
     * 构建一个新的母板并缓存
     * @param masterModel
     */
    public buildMasterService(masterModel: IMasterModel) {
        const masterId = this.getMasterId(masterModel);

        // 如果拿不到masterId，则代表当前页不展示master，不需要构建
        if (!masterId) return;

        // 创建母版
        const masterService = new MasterService(
            this.scale,
            masterModel,
            this.getApplication,
            this.handleStableLayerUpdate,
        );

        // 缓存母版
        this.cacheMaster(masterId, masterService);

        // 如果是为当前页构建，且版式没有隐藏母版元素，则展示母版
        if (this.shouldDisplay(masterModel)) {
            this.updateMaster(masterModel);
            masterService.reloadComponent(masterModel);
        }
        return masterService;
    }

    /**
     * 根据slideModel展示母版，并按需更新组件
     */
    public updateMaster(masterModel: IMasterModel): void {
        const masterId = masterModel ? this.getMasterId(masterModel) : '';

        // 更新当前masterId
        this.currentMasterId = masterId;

        // 展示新母版
        if (masterId && this.shouldDisplay(masterModel)) {
            const masterService = this.masterCacheMap[masterId];
            if (masterService) {
                // 按需刷新母版，由内层判断需不需要重刷
                masterService.updateWithData(this.scale);
            } else {
                // 防止出现找不到 cache 的情况
                this.buildMasterByCache(masterModel);
                this.updateMaster(masterModel);
            }
        }
    }

    /**
     * 更新manager的宽高数据
     * 更新时，如果宽高变化了，只重刷当前母版，其他母版不刷
     * 切页其他母版时，由母版对比自身与manager的宽高是否不同来决定是否重刷
     * */
    public updateWithData(scale: number): void {
        this.scale = scale;
        this.masterCacheMap[this.currentMasterId]?.updateWithData?.(scale);
    }

    /**
     * 获取背景渲染树
     * @param masterModel
     */
    public getRenderComponent = (masterModel: IMasterModel): Container | undefined => {
        let masterService = this.getMasterFromCache(masterModel);
        if (!masterService) {
            masterService = this.buildMasterByCache(masterModel);
        }
        return masterService?.getComponent();
    };

    /**
     * 是否应该展示母版
     * 1. 当前页关联母版是该母版
     * 2. 当前页是幻灯片/版式
     * 3. 版式没有隐藏母版元素
     * @param masterModel
     * @returns
     */
    public shouldDisplay(masterModel: IMasterModel) {
        const { currentSlide } = this.getAppServices().viewableServiceLayer.selectionService;
        let showMasterSp;
        if (currentSlide?.isSlide) {
            showMasterSp = currentSlide.showMasterSp && currentSlide.getRelationLayoutModel()?.showMasterSp;
        } else {
            showMasterSp = currentSlide?.showMasterSp;
        }
        return currentSlide?.getRelationMasterModel() === masterModel && this.isSlideOrLayoutModel(currentSlide)
            && showMasterSp;
    }

    /**
     * 根据slide model获取masterId
     */
    public getMasterId(model) {
        const presentationModel = model?.presentationRef;
        if (presentationModel && model?.sdkData) {
            return presentationModel.getMasterChildrenMapKey(model.sdkData);
        }
    }

    /**
     * 缓存母版canvas元素
     */
    private cacheMaster(masterId, masterService) {
        this.masterCacheMap[masterId] = masterService;
    }

    private isSlideOrLayoutModel(model) {
        if (!model) {
            return false;
        }
        return [UIModelType.SLIDE, UIModelType.LAYOUT].includes(model.type);
    }

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}

